import type { CSSProperties } from 'react'
import type { BankReconRow, BankReconBucket } from './bankReconRows'
import { BUCKET_LABELS } from './bankReconRows'
import type { FieldDefinition, FieldOption } from '@/tableControls'
import { parseBankReconMoney } from './bankReconFormat'

export const BANK_RECON_TABLE_PAGE_KEY = 'bank-recon'

export const DEFAULT_WIDTHS: Record<string, number> = {
  bucket: 150,
  statement_date: 110,
  statement_description: 200,
  statement_amount: 110,
  qbo_date: 110,
  qbo_type_num: 120,
  qbo_name_memo: 200,
  qbo_amount: 110,
  status: 120,
  actions: 220,
}

export const COLUMN_KEYS = [
  'bucket',
  'statement_date',
  'statement_description',
  'statement_amount',
  'qbo_date',
  'qbo_type_num',
  'qbo_name_memo',
  'qbo_amount',
  'status',
  'actions',
] as const

export type BankReconColumnKey = (typeof COLUMN_KEYS)[number]

export const COLUMN_LABELS: Record<BankReconColumnKey, string> = {
  bucket: 'Bucket',
  statement_date: 'Stmt date',
  statement_description: 'Stmt description',
  statement_amount: 'Stmt amount',
  qbo_date: 'QBO date',
  qbo_type_num: 'QBO type / #',
  qbo_name_memo: 'QBO name / memo',
  qbo_amount: 'QBO amount',
  status: 'Status',
  actions: 'Actions',
}

const SORTABLE_COLUMN_KEYS = new Set<BankReconColumnKey>([
  'bucket',
  'statement_date',
  'statement_description',
  'statement_amount',
  'qbo_date',
  'qbo_type_num',
  'qbo_name_memo',
  'qbo_amount',
  'status',
])

export const DEFAULT_SORT = [{ field: 'bucket', direction: 'asc' as const }]

const BUCKET_OPTIONS: FieldOption[] = (
  Object.entries(BUCKET_LABELS) as Array<[BankReconBucket, string]>
).map(([value, label]) => ({ value, label }))

export const FIELD_DEFINITIONS: FieldDefinition[] = [
  {
    key: 'bucket',
    label: COLUMN_LABELS.bucket,
    type: 'select',
    sortable: true,
    filterable: true,
    options: BUCKET_OPTIONS,
    accessor: (row: unknown) => (row as BankReconRow).bucket,
  },
  {
    key: 'statement_date',
    label: COLUMN_LABELS.statement_date,
    type: 'date',
    sortable: true,
    accessor: (row: unknown) => (row as BankReconRow).statementDate ?? '',
  },
  {
    key: 'statement_description',
    label: COLUMN_LABELS.statement_description,
    type: 'text',
    sortable: true,
    filterable: true,
    accessor: (row: unknown) => (row as BankReconRow).statementDescription ?? '',
  },
  {
    key: 'statement_amount',
    label: COLUMN_LABELS.statement_amount,
    type: 'number',
    sortable: true,
    accessor: (row: unknown) => parseBankReconMoney((row as BankReconRow).statementAmount),
  },
  {
    key: 'qbo_date',
    label: COLUMN_LABELS.qbo_date,
    type: 'date',
    sortable: true,
    accessor: (row: unknown) => (row as BankReconRow).qboDate ?? '',
  },
  {
    key: 'qbo_type_num',
    label: COLUMN_LABELS.qbo_type_num,
    type: 'text',
    sortable: true,
    filterable: true,
    accessor: (row: unknown) => {
      const typed = row as BankReconRow
      return [typed.qboType, typed.qboDocNumber].filter(Boolean).join(' ')
    },
  },
  {
    key: 'qbo_name_memo',
    label: COLUMN_LABELS.qbo_name_memo,
    type: 'text',
    sortable: true,
    filterable: true,
    accessor: (row: unknown) => {
      const typed = row as BankReconRow
      return [typed.qboName, typed.qboMemo].filter(Boolean).join(' ')
    },
  },
  {
    key: 'qbo_amount',
    label: COLUMN_LABELS.qbo_amount,
    type: 'number',
    sortable: true,
    accessor: (row: unknown) => parseBankReconMoney((row as BankReconRow).qboAmount),
  },
  {
    key: 'status',
    label: COLUMN_LABELS.status,
    type: 'text',
    sortable: true,
    filterable: true,
    accessor: (row: unknown) => (row as BankReconRow).status ?? '',
  },
  {
    key: 'actions',
    label: COLUMN_LABELS.actions,
    type: 'text',
    sortable: false,
    defaultVisible: true,
    locked: true,
  },
]

export function fixedColumnStyle(width: number): CSSProperties {
  return {
    width,
    minWidth: width,
    maxWidth: width,
    boxSizing: 'border-box',
  }
}

export function isBankReconColumnSortable(key: string): boolean {
  return SORTABLE_COLUMN_KEYS.has(key as BankReconColumnKey)
}

export function buildBankReconFilterFields(): FieldDefinition[] {
  return FIELD_DEFINITIONS.filter((field) => field.filterable)
}
